/* eslint-disable */
/* eslint-disable */
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import throwActionInvalidDebounceThrottleMetaError from '../../../../Errors/Action/ActionInvalidDebounceThrottleMeta.error';
import isFunction from '../../../../Support/Is/Function/IsFunction';
import isNumber from '../../../../Support/Is/Number/IsNumber.support';

/**
 * Create a `handler` for an _observableCallback_ that is capable of conditionally being debounced or throttled
 * @param {CallbackFunction} callback A `callback` to be invoked upon calls from the observable callback
 * @param {Dispatch} dispatch The state managements `dispatch` Function
 * @param {ActionMeta} meta The meta data for the action
 * @param {string} type The `type` of action
 * @param {VoidFunction} updateAction A Function to pass back the newly created action
 * @returns {void} void
 */
const createObservableCallbackHandler = (callback, dispatch, meta, type, updateAction) => {
  const wrappedCallback = isFunction(callback) ? callback : () => {};
  /**
   * Create a `handler` to handle the callback from the _observableCallback_
   * @param {Error | null} error The possible error that this _observableCallback_ encounted
   * @param {ActionMeta} payload The `payload` for this _observableCallback_
   * @returns {void} void
   */
  const handler = (error, payload) => {
    wrappedCallback(error, payload);
    if (error) {
      throw error;
    }
    const action = dispatch({
      complete: false,
      error: null,
      meta,
      payload,
      type,
    });
    updateAction(action);
  };
  const debounceLeading = get(meta, `debounceLeading`, false);
  const debounceTime = get(meta, `debounce`);
  const debounceTrailing = get(meta, `debounceTrailing`, true);
  const throttleLeading = get(meta, `throttleLeading`, true);
  const throttleTime = get(meta, `throttle`);
  const throttleTrailing = get(meta, `throttleTrailing`, true);
  const hasDebounce = isNumber(debounceTime);
  const hasThrottle = isNumber(throttleTime);
  if (hasDebounce && hasThrottle) {
    const action = {
      meta,
      type,
    };
    return throwActionInvalidDebounceThrottleMetaError(action);
  }
  if (hasDebounce) {
    return debounce(handler, debounceTime, {
      leading: debounceLeading,
      trailing: debounceTrailing,
    });
  }
  if (hasThrottle) {
    return throttle(handler, throttleTime, {
      leading: throttleLeading,
      trailing: throttleTrailing,
    });
  }
  return handler;
};

/**
 * Creates an injectable function that will be able to handle the life cycle of an observable callback
 * @param {Store} store The state management `store`
 * @returns {FromObservableCallback} The `dispatch` aware injectable
 */
const createFromObservableCallback = ({ dispatch }) => (
  observableCallback,
  callback,
  meta,
  [FROM_OBSERVABLE_CALLBACK, FROM_OBSERVABLE_CALLBACK_COMPLETE, FROM_OBSERVABLE_CALLBACK_ERROR, FROM_OBSERVABLE_CALLBACK_NEXT],
) => {
  dispatch({
    complete: false,
    error: null,
    meta,
    payload: null,
    type: FROM_OBSERVABLE_CALLBACK,
  });
  /** @type {UnsubscribeFunction<any> | void} */
  let maybeUnsubscribe;
  /** @type {ActionError | ActionSuccess} */
  let errorOrSuccessAction;
  /**
   * Update `errorOrSuccessAction` with the new `action` returned from the handle created by `createObservableCallbackHandler`
   * @param {ActionSuccess} newAction The `action` to use to update `errorOrSuccessAction`
   * @returns {void} void
   */
  const updateAction = (newAction) => {
    errorOrSuccessAction = newAction;
  };
  try {
    maybeUnsubscribe = observableCallback(createObservableCallbackHandler(callback, dispatch, meta, FROM_OBSERVABLE_CALLBACK_NEXT, updateAction));
  } catch (error) {
    errorOrSuccessAction = dispatch({
      complete: false,
      error,
      meta,
      payload: null,
      type: FROM_OBSERVABLE_CALLBACK_ERROR,
    });
  }
  return () => {
    return Promise.resolve()
      .then(() => {
        /* eslint-disable-next-line promise/always-return */ // The return value here doesn't matter
        if (isFunction(maybeUnsubscribe)) {
          maybeUnsubscribe();
        }
      })
      .then(() => {
        return dispatch({
          ...errorOrSuccessAction,
          complete: true,
          type: FROM_OBSERVABLE_CALLBACK_COMPLETE,
        });
      });
  };
};

export default createFromObservableCallback;
